import { FC, useEffect, useState } from "react";
import "./Checkout.scss";
import { Card, Col, Container, FormGroup, Row } from "react-bootstrap";
import MainNavbar from "../../components/MainNavbar/MainNavbar";
import ErrorMessage from "../../components/ErrorMessage/ErrorMessage";
import { useAuth } from "../../providers/AuthProvider";
import { set, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { schema } from "./Validate";
import { BvIcon } from "bevi-icon";
import { useCheckout } from "../../providers/Checkout/CheckoutProvider";
import FormatNumber from "../../utils/FormatNumber";
import logo from "../../assets/img/bevi-ajuda-blue.svg";
import { InputMask } from "@react-input/mask";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCoupons } from "../../providers/Financial/Coupons/CouponsProvider";
import FormatMoney from "../../utils/FormatMoney";

interface CheckoutProps {}

const Checkout: FC<CheckoutProps> = () => {
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [isloading, setIsLoading] = useState(true);
  const [paymentType, setPaymentType] = useState<"credit" | "debit">("credit");
  const [isTerms, setIsTerms] = useState(false);
  const [coupon, setCoupon] = useState<any>("");
  const [iscoupon, setIsCoupon] = useState<boolean>(false);

  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const { onInvalid } = useAuth();
  const {
    articlePlatform,
    handleListArticlesPlatform,
    handleFetch,
    isLoading,
  } = useCheckout();

  const { handleValidate, validateCoupon } = useCoupons();

  const {
    handleSubmit,
    register,
    setValue,
    reset,
    control,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    const checkoutOk = localStorage.getItem("checkoutOk");

    if (!checkoutOk) {
      navigate("/pricing/list", { replace: true });
      return;
    }

    handleListArticlesPlatform();
    const loadData = async () => {
      setIsLoading(true);

      await new Promise((resolve) => setTimeout(resolve, 2000));

      const rawPlan = localStorage.getItem("selectedPlan");

      if (rawPlan) {
        try {
          const parsed = JSON.parse(rawPlan);
          setSelectedPlan(parsed);
          setValue("plan_id", parsed?.plan_id);
          setValue("cycle", parsed?.typePlan);
          setValue("payment_method", "card");
        } catch (err) {
          console.error(err);
        }
      }

      setIsLoading(false);
    };

    loadData();
  }, []);

  const onSubmit = async (data: any) => {
    if (!stripe || !elements) return;

    const cardNumberElement = elements.getElement(CardNumberElement);

    if (!cardNumberElement) return;

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardNumberElement,
    });

    if (error) {
      toast.error(error.message);
      return;
    }

    const upt = {
      plan_id: data?.plan_id,
      cycle: data?.cycle === "Anual" ? "annual" : "monthly",
      payment_method: data?.payment_method,
      payment_method_id: paymentMethod.id,
    };

    const ret = await handleFetch(upt);

    if (ret?.success) {
      setIsCoupon(false);
      localStorage.setItem("checkoutFinished", "true");
      navigate(`/checkout/completed/${ret?.id}`);
    }
  };

  const handleSelectPayment = (type: "credit" | "debit") => {
    setPaymentType(type);
  };

  const handleClickTerms = (e: any) => {
    setValue("terms", e?.target?.checked);
    setIsTerms(e?.target?.checked);
  };

  const handleApplyCoupon = async () => {
    if (!coupon) return toast.error("Informe o cupom!");

    const ret = await handleValidate({
      identifier: coupon,
      plan_id: selectedPlan?.plan_id,
      cycle: selectedPlan?.typePlan === "Anual" ? "annual" : "monthly",
    });

    if (ret) {
      setIsCoupon(true);
    }
  };

  const handleClickDeletCoupon = () => {
    setCoupon("");
    setValue("coupon_identifier", "");
    setIsCoupon(false);
  };

  return (
    <>
      <MainNavbar />
      <div className="Checkout" data-testid="Checkout">
        {!isloading ? (
          <Container>
            <Card className="mt-4 info-form">
              <Card.Body>
                <Row className="justify-content-center">
                  <Col className="me-5" md={5} sm={12}>
                    <div>
                      <h1>{selectedPlan?.name}</h1>
                      <div className="d-flex justify-content-between">
                        <h3>até {selectedPlan?.max_users} usuários</h3>
                        <span className="monthly">
                          {selectedPlan?.typePlan}
                        </span>
                      </div>
                    </div>
                    <hr />
                    <div>
                      <h3>Informações de faturamento</h3>
                      <Row>
                        <Col md={12}>
                          <FormGroup className="form-group mt-3">
                            <label htmlFor="name">Seu nome *</label>
                            <input
                              className={`form-control ${onInvalid(errors?.name)}`}
                              type="text"
                              placeholder="BeviOficial"
                              {...register("name")}
                              onChange={(e: any) =>
                                setValue("name", e?.target?.value)
                              }
                            />
                            <ErrorMessage
                              name={"Nome"}
                              type={errors?.name?.type?.toString()}
                              min={3}
                              max={100}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={12}>
                          <FormGroup className="form-group mt-3">
                            <label htmlFor="email">Email *</label>
                            <input
                              className={`form-control ${onInvalid(errors?.email)}`}
                              type="text"
                              placeholder="bevi@gmail.com"
                              {...register("email")}
                              onChange={(e: any) =>
                                setValue("email", e?.target?.value)
                              }
                            />
                            <ErrorMessage
                              name={"E-mail"}
                              type={errors?.email?.type?.toString()}
                              min={3}
                              max={100}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={12}>
                          <FormGroup className="form-group mt-3">
                            <label htmlFor="phone">Telefone *</label>
                            <InputMask
                              className={`form-control ${onInvalid(errors?.phone)}`}
                              mask="(00) 00000-0000"
                              replacement={{ 0: /\d/ }}
                              placeholder="(00) 00000-0000"
                              {...register("phone")}
                            />

                            <ErrorMessage
                              name={"Telefone"}
                              type={errors?.phone?.type}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                    </div>
                    <hr className="mt-4 mb-4" />
                    <div className="mt-3">
                      <h3>Pagamento</h3>
                      <span className="description">
                        Escolha a forma de pagamento:
                      </span>
                      <Row className="mt-4">
                        <Col md={6}>
                          <Card className="payment-method-card">
                            <div className="d-flex align-items-center">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  value=""
                                  id="flexCheckDefault"
                                  checked={paymentType === "credit"}
                                  onChange={() => handleSelectPayment("credit")}
                                />
                                <label className="form-check-label payment-method">
                                  Cartão de Crédito
                                </label>
                              </div>
                              <div>
                                <BvIcon
                                  name="creditCard"
                                  variant="duo"
                                  width={32}
                                  height={32}
                                  className="ms-2"
                                />
                              </div>
                            </div>
                          </Card>
                        </Col>
                        <Col md={6}>
                          <Card className="payment-method-card">
                            <div className="d-flex align-items-center">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  value=""
                                  id="flexCheckDefault"
                                  checked={paymentType === "debit"}
                                  onChange={() => handleSelectPayment("debit")}
                                />
                                <label className="form-check-label payment-method">
                                  Cartão de Débito
                                </label>
                              </div>
                              <div>
                                <BvIcon
                                  name="creditCard"
                                  variant="solid"
                                  width={32}
                                  height={32}
                                  className="ms-2"
                                />
                              </div>
                            </div>
                          </Card>
                        </Col>
                      </Row>
                      {paymentType === "debit" && (
                        <div className="debit-warning mt-2 d-flex align-items-center gap-2">
                          <BvIcon name="info" variant="light" width={24} height={24} color="#222343" />
                          <span style={{ fontSize: "12px",fontWeight: 400, color: "#222343" }}>Cartões com função débito/crédito serão cobrados na função crédito.</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <Row>
                        <Col md={12}>
                          <FormGroup className="form-group mt-3">
                            <label htmlFor="numberCard">
                              Número do cartão *
                            </label>
                            <CardNumberElement
                              onChange={(e) => {
                                if (e.error) console.log(e.error.message);
                              }}
                              className="form-control"
                            />
                          </FormGroup>
                        </Col>
                        <Col md={12}>
                          <FormGroup className="form-group mt-3">
                            <label htmlFor="titular">
                              Nome do titular do cartão *
                            </label>
                            <input
                              className={`form-control ${onInvalid(errors?.titular)}`}
                              type="text"
                              placeholder="Inserir nome completo"
                              {...register("titular")}
                              onChange={(e: any) =>
                                setValue("titular", e?.target?.value)
                              }
                            />
                            <ErrorMessage
                              name={"Nome do titular do cartão"}
                              type={errors?.titular?.type}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup className="form-group mt-3">
                            <label htmlFor="expirationDate">
                              Data de Validade *
                            </label>
                            <CardExpiryElement
                              onChange={(e) => {
                                if (e.error) console.log(e.error.message);
                              }}
                              className="form-control"
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup className="form-group mt-3">
                            <label htmlFor="cvv">CVV *</label>
                            <CardCvcElement
                              onChange={(e) => {
                                if (e.error) console.log(e.error.message);
                              }}
                              className="form-control"
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                    </div>
                    <div className="d-flex align-items-center mt-4">
                      <BvIcon name="lock" width={24} height={24} />
                      <span className="ssl-payment ms-2">
                        Pagamento seguro com criptografia SSL
                      </span>
                    </div>
                  </Col>
                  <Col className="ms-5" md={5} sm={12}>
                    <div className="payment-information">
                      <h3 className="lh-1">
                        Com o {selectedPlan?.name} você obtém
                      </h3>
                      <div className="d-flex align-items-center mt-4">
                        <div>
                          <BvIcon
                            variant={"duo"}
                            name="article"
                            width={24}
                            height={24}
                            className="me-2"
                          />
                        </div>

                        <h5>Plataforma de Artigos</h5>
                      </div>
                      {articlePlatform?.map((art: any) => (
                        <div className="d-flex mt-3 align-items-center">
                          <div>
                            <BvIcon
                              variant={"light"}
                              name="checkCircle"
                              width={24}
                              height={24}
                            />
                          </div>
                          <span className="description ms-2">
                            {art?.article}
                          </span>
                        </div>
                      ))}
                      <hr className="mt-4 mb-4" />

                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex flex-column">
                          <h3>Faturamento</h3>
                          <span className="description">
                            Cobrança {selectedPlan?.typePlan}
                          </span>
                        </div>
                        <span className="value">
                          R${FormatNumber(selectedPlan?.price)}
                        </span>
                      </div>

                      {iscoupon && (
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex flex-column">
                            <span className="description">
                              Cupom aplicado: {coupon}
                            </span>
                          </div>
                          <span className="value">
                            -R${FormatNumber(validateCoupon?.discount)}
                          </span>
                        </div>
                      )}

                      <hr className="mt-4 mb-4" />
                      <Row>
                        <Col md={12}>
                          <FormGroup className="form-group">
                            <label htmlFor="discountCoupon">
                              Tem cupom de desconto?
                            </label>
                            <div className="d-flex align-items-center">
                              <input
                                className={`form-control ${onInvalid(errors?.coupon_identifier)}`}
                                type="text"
                                placeholder="Digitar cupom de desconto"
                                {...register("coupon_identifier")}
                                onChange={(e: any) => {
                                  setCoupon(e?.target?.value);
                                  setValue(
                                    "coupon_identifier",
                                    e?.target?.value,
                                  );
                                }}
                                disabled={iscoupon}
                              />
                              <div className="ms-2">
                                <button
                                  onClick={() => handleApplyCoupon()}
                                  className="button-apply"
                                >
                                  Ok
                                </button>
                              </div>
                            </div>
                            {iscoupon && (
                              <div className="tagCoupon mt-2">
                                <span className="ms-2">{coupon}</span>
                                <a
                                  onClick={() => handleClickDeletCoupon()}
                                  className="tagClose"
                                >
                                  <BvIcon
                                    name="close"
                                    weight={600}
                                    width={12}
                                    height={12}
                                    className="close-icon"
                                  />
                                </a>
                              </div>
                            )}
                          </FormGroup>
                        </Col>
                      </Row>
                      <hr className="mt-4 mb-4" />
                      <div>
                        <div className="d-flex align-items-center justify-content-between mb-4">
                          <div className="d-flex flex-column">
                            <h3>Total</h3>
                            <span className="description">
                              Cobrança {selectedPlan?.typePlan}
                            </span>
                          </div>

                          <span className="total-price">
                            {iscoupon ? (
                              <span>
                                {FormatMoney(
                                  Number(selectedPlan?.price) -
                                    Number(validateCoupon?.discount),
                                )}
                              </span>
                            ) : (
                              <div>{FormatMoney(selectedPlan?.price)}</div>
                            )}
                          </span>
                        </div>
                        <div className="form-check mb-3">
                          <FormGroup className="form-group">
                            <input
                              className={`form-check-input ${onInvalid(errors?.terms)}`}
                              type="checkbox"
                              value=""
                              id="flexCheckDefault"
                              onClick={(e) => handleClickTerms(e)}
                            />
                            <label
                              className={`form-check-label privacy-policy`}
                              style={{ color: errors?.terms ? "red" : "" }}
                            >
                              Li e concordo com os termos da Política de
                              Privacidade.
                            </label>
                            <ErrorMessage
                              name={"Termo"}
                              type={errors?.terms?.type}
                            />
                          </FormGroup>
                        </div>
                      </div>
                      <div className="d-flex justify-content-center">
                        <button
                          onClick={handleSubmit(onSubmit)}
                          className="mb-5 button-checkout"
                          style={{
                            cursor: !isLoading ? "pointer" : "not-allowed",
                            opacity: !isLoading ? "1" : "0.75",
                          }}
                          disabled={isLoading}
                        >
                          {!isLoading ? (
                            <span>Confirmar e pagar</span>
                          ) : (
                            <>
                              <FontAwesomeIcon
                                className="me-2"
                                icon={faSpinner}
                                spin
                              />
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Container>
        ) : (
          <div className="loading-screen">
            <div className="loading-content">
              <img src={logo} alt="Logo" className="logo" />
              <div className="loader-bar" />
              <p className="loading-text">Carregando...</p>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Checkout;
